/**
 * Prompt Button Component
 *
 * Travel suggestion pill with chat_round_call icon
 * Based on Figma design: node-id=250-2899
 * 100% Design System Integration - NO hardcoded values
 */

'use client';

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import {
  typographyColors,
  backgroundColors,
  spacing,
  borders,
  textStyles,
  typography,
  fontPrimary,
} from '@/design-system/utils';

const TOOLTIP_GAP = 8;

interface PromptButtonProps {
  prompt: string;
  onClick?: () => void;
  className?: string;
}

export function PromptButton({
  prompt,
  onClick,
  className = '',
}: PromptButtonProps) {
  const textRef = useRef<HTMLSpanElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [needsTooltip, setNeedsTooltip] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    left: number;
    top: number;
    below: boolean;
  } | null>(null);

  const checkTruncation = useCallback(() => {
    if (!textRef.current) return;
    const el = textRef.current;
    const isTruncated = el.scrollWidth > el.clientWidth;
    setNeedsTooltip(isTruncated);
  }, []);

  const updateTooltipPosition = useCallback(() => {
    if (!wrapperRef.current || !showTooltip) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    const below = spaceAbove < spaceBelow;
    const centerX = rect.left + rect.width / 2;
    setTooltipPosition({
      left: centerX,
      top: below ? rect.bottom + TOOLTIP_GAP : rect.top - TOOLTIP_GAP,
      below,
    });
  }, [showTooltip]);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const t1 = setTimeout(checkTruncation, 0);
    const t2 = setTimeout(checkTruncation, 100);
    const t3 = setTimeout(checkTruncation, 400);

    const resizeObserver = new ResizeObserver(checkTruncation);
    resizeObserver.observe(el);
    window.addEventListener('resize', checkTruncation);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkTruncation);
    };
  }, [prompt, checkTruncation]);

  // Keep tooltip position in sync when scrolling or resizing (so it works on small viewports)
  useEffect(() => {
    if (!showTooltip || !needsTooltip) return;
    updateTooltipPosition();
    window.addEventListener('scroll', updateTooltipPosition, true);
    window.addEventListener('resize', updateTooltipPosition);
    return () => {
      window.removeEventListener('scroll', updateTooltipPosition, true);
      window.removeEventListener('resize', updateTooltipPosition);
    };
  }, [showTooltip, needsTooltip, updateTooltipPosition]);

  const handleMouseEnter = () => {
    if (needsTooltip && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      setTooltipPosition({
        left: rect.left + rect.width / 2,
        top:
          spaceAbove < spaceBelow
            ? rect.bottom + TOOLTIP_GAP
            : rect.top - TOOLTIP_GAP,
        below: spaceAbove < spaceBelow,
      });
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
    setTooltipPosition(null);
  };

  const tooltipEl =
    showTooltip &&
    needsTooltip &&
    tooltipPosition &&
    typeof document !== 'undefined' ? (
      <div
        role='tooltip'
        style={{
          position: 'fixed',
          left: tooltipPosition.left,
          top: tooltipPosition.below ? tooltipPosition.top : undefined,
          bottom: tooltipPosition.below
            ? undefined
            : window.innerHeight - tooltipPosition.top - TOOLTIP_GAP,
          transform: 'translateX(-50%)',
          padding: `${spacing[2]} ${spacing[3]}`,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          color: '#ffffff',
          borderRadius: borders.radius.md,
          fontSize: typography.size.sm,
          fontFamily: fontPrimary,
          whiteSpace: 'nowrap',
          zIndex: 99999,
          pointerEvents: 'none',
          maxWidth: 'min(400px, calc(100vw - 24px))',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        }}
      >
        {prompt}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            ...(tooltipPosition.below
              ? {
                  bottom: '100%',
                  borderBottom: '6px solid rgba(0, 0, 0, 0.85)',
                }
              : {
                  top: '100%',
                  borderTop: '6px solid rgba(0, 0, 0, 0.85)',
                }),
          }}
        />
      </div>
    ) : null;

  return (
    <div
      ref={wrapperRef}
      style={{ position: 'relative', width: '100%' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type='button'
        title={needsTooltip ? prompt : undefined}
        onClick={onClick}
        style={{
          backgroundColor: backgroundColors.primary,
          borderColor: backgroundColors.shade,
          borderWidth: borders.width.thin,
          borderStyle: 'solid',
          paddingLeft: spacing[4],
          paddingRight: spacing[4],
          paddingTop: spacing[2],
          paddingBottom: spacing[2],
          borderRadius: borders.radius.lg,
          gap: spacing[2],
          cursor: 'pointer',
          transition: 'background-color 0.2s',
          overflow: 'hidden',
          minWidth: 0,
          width: '100%',
        }}
        className={`flex items-center justify-start hover:opacity-90 ${className}`}
        onMouseEnter={e => {
          e.currentTarget.style.backgroundColor = backgroundColors.secondary;
        }}
        onMouseLeave={e => {
          e.currentTarget.style.backgroundColor = backgroundColors.primary;
        }}
      >
        <Image
          src='/assets/images/icons/chat_round_call.svg'
          alt=''
          width={20}
          height={20}
          className='shrink-0'
        />
        <span
          ref={textRef}
          className='text-left'
          style={{
            ...textStyles.caption,
            color: typographyColors.primary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
            flex: 1,
          }}
        >
          {prompt}
        </span>
      </button>

      {typeof document !== 'undefined' && tooltipEl
        ? createPortal(tooltipEl, document.body)
        : null}
    </div>
  );
}

export default PromptButton;
